import {
  SNCB2,
  SNCB1,
  SNCF2,
  SNCF1SP,
  SNCF1BP,
  LYRIA1,
  LYRIA2,
  LYRIABP,
  PLUS,
  PREMIER,
  STANDARD,
  STD,
  SP,
  BP,
  ER_STANDARD,
  COMFORT,
  PREMIUM,
  WC_COMP,
  LYRIAWC,
  SNCFWC,
  EVAC,
  UNASSIGNED,
  SNCF_OPTIMUM,
  SNCF_OPTIMUMPLUS,
  DB1,
  DB2,
  NS1,
  NS2
} from 'constants/classOfService';
import { Core, LyriaColors, SncfColors, SncbColors, Messaging, Greyscale, Opacity, Shades, DBColours, NSColours } from './colours';
import Fonts from './fonts';

const fontFamily = Fonts.fontFamily.ABCSocial;
const fontSizeRegular = '1rem';
const fontSizeLarge = '1.25rem';
const baseSize = '16px';
const htmlSizeReset = '';

const fontStyleRegular = `
  color: ${Core.CoreNavy};
`;

const fonts = {
  ...Fonts,
  fontFamily,
  fontSizeRegular,
  fontSizeLarge,
  fontStyleRegular,
  baseSize,
  htmlSizeReset
};

const colours = { Core, LyriaColors, Greyscale, Shades, Opacity, Messaging };

// Here are some very common reused styles
const primaryButton = {
  fontColour: Greyscale.Charcoal,
  activeFontColour: Greyscale.Charcoal,
  backgroundColour: Core.SecondaryYellow,
  hoverBackgroundColour: Shades.YellowShade20,
  activeBackgroundColour: Shades.YellowShade40,
  focusBackgroundColour: Core.SecondaryYellow,
  borderColour: Greyscale.White.opacity(0),
  hoverBorderColour: Greyscale.White.opacity(0),
  focusBorderColour: Greyscale.Charcoal,
  activeBorderColour: Greyscale.White.opacity(0)
};
const secondaryButton = {
  fontColour: Greyscale.White,
  hoverFontColour: Greyscale.White,
  backgroundColour: Core.CoreBlue,
  hoverBackgroundColour: Shades.BlueShade40,
  activeBackgroundColour: Shades.BlueShade60,
  borderColour: Greyscale.White.opacity(0),
  focusBorderColour: Greyscale.White,
  activeBorderColour: Greyscale.White.opacity(0)
};
const tertiaryButton = {
  fontColour: Core.CoreBlue,
  backgroundColour: Greyscale.White,
  borderColour: Core.CoreBlue,
  focusBackgroundColour: Greyscale.White,
  focusBorderColour: Core.CoreBlue,
  focusFontColour: Core.CoreBlue,
  activeBackgroundColour: Shades.BlueShade60,
  activeFontColour: Greyscale.White,
  activeBorderColour: Greyscale.White.opacity(0),
  hoverBackgroundColour: Shades.BlueShade40,
  hoverFontColour: Greyscale.White,
  hoverBorderColour: Greyscale.White.opacity(0)
};
const tertiaryBlue20 = {
  fontColour: Shades.BlueShade20,
  backgroundColour: Greyscale.White.opacity(0),
  borderColour: Shades.BlueShade20,
  focusBorderColour: Shades.BlueShade20,
  activeBackgroundColour: Shades.BlueShade60,
  activeFontColour: Greyscale.White,
  activeBorderColour: Greyscale.White.opacity(0),
  hoverBackgroundColour: Shades.BlueShade40,
  hoverFontColour: Greyscale.White,
  hoverBorderColour: Greyscale.White.opacity(0)
};
const tertiaryLink = {
  fontColour: Shades.BlueShade20,
  backgroundColour: 'transparent',
  borderColour: 'transparent'
};
const disabledButton = {
  fontColour: Greyscale.SteelGrey,
  backgroundColour: Greyscale.SilverGrey,
  borderColour: Greyscale.White.opacity(0),
  focusBorderColour: Greyscale.SteelGrey,
  activeBackgroundColour: Greyscale.SilverGrey,
  activeBorderColour: Greyscale.White.opacity(0),
  hoverBackgroundColour: Greyscale.SilverGrey,
  hoverBorderColour: Greyscale.White.opacity(0)
};

const seats = {
  available: {
    backgroundColour: 'transparent',
    fontColour: Greyscale.Eggshell,
    iconColour: Core.CoreBlue
  },
  assigned: {
    backgroundColour: Core.SecondaryYellow,
    fontColour: Greyscale.Eggshell,
    iconColour: Greyscale.Black
  },
  selected: {
    backgroundColour: Core.SecondaryYellow,
    fontColour: Greyscale.Eggshell,
    iconColour: Greyscale.Black,
    strokeColour: Greyscale.Black
  },
  unavailable: {
    backgroundColour: Greyscale.SilverGrey,
    fontColour: Greyscale.SilverGrey,
    iconColour: Greyscale.Eggshell,
    lineColour: Greyscale.CloudGrey
  },
  nonEditable: {
    backgroundColour: Messaging.WarningOrange,
    fontColour: Greyscale.Eggshell,
    iconColour: Greyscale.Black
  },
  blocked: {
    backgroundColour: Messaging.ErrorRed,
    fontColour: Greyscale.Eggshell,
    iconColour: Greyscale.Black
  }
};

const common = {
  buttons: {
    primary: primaryButton,
    secondary: secondaryButton,
    tertiary: tertiaryBlue20,
    tertiaryVariant: tertiaryButton,
    tertiaryLink: tertiaryLink,
    disabled: disabledButton
  },
  notifications: {
    information: {
      backgroundColour: Messaging.InfoBlueLight,
      borderColour: Messaging.InfoBlue,
      fontColour: Greyscale.Black,
      icon: 'information'
    },
    alert: {
      backgroundColour: Messaging.WarningOrangeLight,
      borderColour: Messaging.WarningOrange,
      fontColour: Greyscale.Black,
      icon: 'warningTriangle'
    },
    success: {
      backgroundColour: Messaging.SuccessGreenLight,
      borderColour: Messaging.SuccessGreen,
      fontColour: Greyscale.Black,
      icon: 'tickInCircle'
    },
    error: {
      backgroundColour: Messaging.ErrorRedLight,
      borderColour: Messaging.ErrorRed,
      fontColour: Greyscale.Black,
      icon: 'exclamation'
    }
  },
  seatmaps: {
    es: { ...seats },
    er: { ...seats }
  },
  paymentActions: {
    payment: {
      backgroundColour: Core.CoreNavy,
      fontColour: Greyscale.White
    },
    refund: {
      backgroundColour: Core.SecondaryYellow,
      fontColour: Greyscale.Black
    }
  },
  reactSwitch: {
    // React Switch requires plain strings
    onColor: Shades.BlueShade20.toString(),
    offColor: Greyscale.SteelGrey.toString(),
    offHandleColor: Greyscale.White.toString(),
    onHandleColor: Greyscale.White.toString()
  },
  genericUiHighlightColour: Shades.BlueShade20,
  tables: {
    header: {
      backgroundColour: Messaging.InfoBlueLight,
      fontColour: Greyscale.Black
    },
    row: {
      backgroundColour: 'transparent',
      fontColour: Greyscale.Black
    },
    rowAlternate: {
      backgroundColour: Greyscale.MistGrey,
      fontColour: Greyscale.Black
    },
    footer: {
      backgroundColour: Greyscale.SilverGrey,
      fontColour: Greyscale.Black
    }
  },
  bigAccordionButtons: {
    backgroundColour: Shades.BlueShade40,
    fontColour: Greyscale.White
  },
  pagination: {
    normal: {
      backgroundColour: 'transparent',
      borderColour: 'transparent',
      fontColour: Shades.BlueShade20,
      outlineColour: 'transparent'
    },
    hover: {
      backgroundColour: 'transparent',
      borderColour: 'transparent',
      fontColour: Shades.BlueShade20,
      outlineColour: 'transparent'
    },
    active: {
      backgroundColour: Shades.BlueShade20,
      borderColour: Shades.BlueShade20,
      fontColour: Greyscale.White,
      outlineColour: Shades.BlueShade20
    },
    focus: {
      backgroundColour: 'transparent',
      borderColour: Shades.BlueShade20,
      fontColour: Shades.BlueShade20,
      outlineColour: Greyscale.White
    },
    activeFocus: {
      backgroundColour: 'transparent',
      borderColour: Greyscale.White,
      fontColour: Shades.BlueShade20,
      outlineColour: Shades.BlueShade20
    },
    disabled: {
      backgroundColour: 'transparent',
      borderColour: 'transparent',
      fontColour: Greyscale.SmokeGrey,
      outlineColour: 'transparent'
    }
  },
  tabs: {
    baseBorder: Greyscale.CloudGrey,
    active: {
      backgroundColour: 'transparent',
      borderColour: 'transparent',
      fontColour: Greyscale.Charcoal,
      highlightColour: Shades.BlueShade40
    },
    normal: {
      backgroundColour: 'transparent',
      borderColour: 'transparent',
      fontColour: Greyscale.SteelGrey,
      highlightColour: 'transparent'
    },
    disabled: {
      backgroundColour: Greyscale.SilverGrey,
      borderColour: 'transparent',
      fontColour: Greyscale.SteelGrey,
      highlightColour: 'transparent'
    }
  },
  searchResults: {
    header: {
      default: {
        backgroundColour: Messaging.InfoBlueLight,
        fontColour: Greyscale.Black
      },
      [STANDARD]: {
        backgroundColor: Core.SecondaryGreen,
        fontColour: Greyscale.White
      },
      [PLUS]: {
        backgroundColor: Core.SecondaryPlum,
        fontColour: Greyscale.White
      },
      [PREMIER]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [STD]: {
        backgroundColor: Core.SecondaryGreen,
        fontColour: Greyscale.White
      },
      [SP]: {
        backgroundColor: Core.SecondaryPlum,
        fontColour: Greyscale.White
      },
      [BP]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [ER_STANDARD]: {
        backgroundColor: Core.SecondaryGreen,
        fontColour: Greyscale.White
      },
      [COMFORT]: {
        backgroundColor: Core.SecondaryPlum,
        fontColour: Greyscale.White
      },
      [PREMIUM]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [LYRIA2]: {
        backgroundColor: LyriaColors.BleuGlacier,
        fontColour: Greyscale.White
      },
      [LYRIA1]: {
        backgroundColor: LyriaColors.Coquelicot,
        fontColour: Greyscale.White
      },
      [LYRIABP]: {
        backgroundColor: LyriaColors.Ardoise,
        fontColour: Greyscale.White
      },
      [SNCF2]: {
        backgroundColor: SncfColors.PaleBlueLily,
        fontColour: Greyscale.Black
      },
      [SNCF1SP]: {
        backgroundColor: SncfColors.CoralBlue,
        fontColour: Greyscale.Black
      },
      [SNCF1BP]: {
        backgroundColor: SncfColors.PaleCerulean,
        fontColour: Greyscale.Black
      },
      [SNCB2]: {
        backgroundColor: SncbColors.VividBlue,
        fontColour: Greyscale.White
      },
      [SNCB1]: {
        backgroundColor: SncbColors.MidnightBlue,
        fontColour: Greyscale.White
      },
      [DB2]: {
        backgroundColor: DBColours.secondClass,
        fontColour: Greyscale.White
      },
      [DB1]: {
        backgroundColor: DBColours.firstClass,
        fontColour: Greyscale.White
      },
      [NS2]: {
        backgroundColor: NSColours.secondClass,
        fontColour: Greyscale.White
      },
      [NS1]: {
        backgroundColor: NSColours.firstClass,
        fontColour: Greyscale.White
      },
      [WC_COMP]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [LYRIAWC]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [SNCFWC]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [EVAC]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [UNASSIGNED]: {
        backgroundColor: Core.CoreNavy,
        fontColour: Greyscale.White
      },
      [SNCF_OPTIMUM]: {
        backgroundColor: SncfColors.PaleCerulean,
        fontColour: Greyscale.Black
      },
      [SNCF_OPTIMUMPLUS]: {
        backgroundColor: SncfColors.PaleCerulean,
        fontColour: Greyscale.Black
      }
    },
    row: {
      default: {
        backgroundColour: 'transparent',
        fontColour: Greyscale.Black
      },
      [STANDARD]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.GreenTint90,
        focusBorderColour: Core.SecondaryGreen,
        fontColour: Greyscale.Black
      },
      [PLUS]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.PlumTint90,
        focusBorderColour: Core.SecondaryPlum,
        fontColour: Greyscale.Black
      },
      [PREMIER]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.NavyTint90,
        focusBorderColour: Core.CoreNavy,
        fontColour: Greyscale.Black
      },
      [WC_COMP]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.NavyTint90,
        focusBorderColour: Core.CoreNavy,
        fontColour: Greyscale.Black
      },
      [EVAC]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.NavyTint90,
        focusBorderColour: Core.CoreNavy,
        fontColour: Greyscale.Black
      },
      [UNASSIGNED]: {
        backgroundColour: 'transparent',
        hoverBackgroundColour: Shades.NavyTint90,
        focusBorderColour: Core.CoreNavy,
        fontColour: Greyscale.Black
      },
      disabled: {
        fontColour: Greyscale.SteelGrey
      }
    }
  },
  homepageCards: {
    header: {
      backgroundColour: Shades.BlueShade40,
      fontColour: Greyscale.Eggshell
    },
    image: {
      backgroundColour: Greyscale.Eggshell
    }
  },
  classes: {
    default: Core.CoreNavy,
    [STANDARD]: Core.SecondaryGreen,
    [PLUS]: Core.SecondaryPlum,
    [PREMIER]: Core.CoreNavy,
    [ER_STANDARD]: Core.SecondaryGreen,
    [COMFORT]: Core.SecondaryPlum,
    [PREMIUM]: Core.CoreNavy,
    [STD]: Core.SecondaryGreen,
    [SP]: Core.SecondaryPlum,
    [BP]: Core.CoreNavy,
    [LYRIA2]: LyriaColors.BleuGlacier,
    [LYRIA1]: LyriaColors.Coquelicot,
    [LYRIABP]: LyriaColors.Ardoise,
    [WC_COMP]: Core.CoreNavy,
    [LYRIAWC]: Core.CoreNavy,
    [SNCFWC]: Core.CoreNavy,
    [SNCF2]: SncfColors.PaleBlueLily,
    [SNCF1SP]: SncfColors.CoralBlue,
    [SNCF1BP]: SncfColors.PaleCerulean,
    [SNCB2]: SncbColors.VividBlue,
    [SNCB1]: SncbColors.MidnightBlue,
    [EVAC]: Core.CoreNavy,
    [SNCF_OPTIMUM]: SncfColors.PaleCerulean,
    [SNCF_OPTIMUMPLUS]: SncfColors.PaleCerulean,
    [DB1]: DBColours.firstClass,
    [DB2]: DBColours.secondClass,
    [NS1]: NSColours.firstClass,
    [NS2]: NSColours.secondClass,
    es: {
      std: Core.SecondaryGreen,
      sp: Core.SecondaryPlum,
      bp: Core.CoreNavy
    },
    er: {
      standard: Core.SecondaryGreen,
      comfort: Core.SecondaryPlum,
      premium: Core.CoreNavy
    }
  },
  clubEurostar: {
    classique: Core.CoreBlue,
    carteBlanche: Core.CoreCream,
    avantage: Shades.PlumTint30,
    elite: Greyscale.CharcoalGrey,
    etoile: Greyscale.CharcoalGrey
  },
  spinners: {
    colour: Core.CoreBlue,
    loadingButtonSize: 16,
    pageLoaderSize: 30
  }
};

export { colours, common, fonts };
